import moment from "moment";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ApiMessage } from "src/api/types";
import Card from "src/components/Card";
import Indicator from "src/components/layout/Indicator";
import ListItem from "src/components/lists/list-items/ListItem";
import Skeleton from "src/components/Skeleton";
import TitleCut from "src/components/TitleCut";
import useUpdateEffect from "src/hooks/useUpdateEffect";
import { formatPrice } from "src/lib/formatters";
import useAuth from "src/hooks/selectors/useAuth";

interface InboxListItemProps {
	url: string;
	user: {
		name: string;
		company_name?: string;
		avatar?: string;
	};
	subject?: string;
	message?: ApiMessage;
	variant: "dark" | "light";
	unread_count: number;
	theme: "card" | "list-item";
}

const InboxListItem = ({
	url,
	user,
	subject,
	message,
	unread_count,
	theme,
}: InboxListItemProps) => {
	const auth = useAuth();
	const { t } = useTranslation("lists", {
		keyPrefix: "inbox-list",
	});
	const location = useLocation();
	const [count, setCount] = useState(unread_count);
	let excerpt = message?.excerpt || message?.content;
	let content = excerpt;

	useUpdateEffect(() => {
		//Set count to 0 if the chat is opened
		if (location.pathname === url) {
			setCount(0);
		}
	}, [location]);

	if (message) {
		if (!content && (message.media || []).length) {
			let count = message?.media?.length || 0;
			excerpt = t("item.files", { count });
		}

		if (message?.data?.bid) {
			const bid = message?.data?.bid;
			excerpt = t("item.bid.unknown");
			if (bid?.status === "ACCEPTED") {
				excerpt = t("item.bid.accepted", {
					price: formatPrice(bid.budget),
				});
			}
			if (bid?.status === "DENIED") {
				excerpt = t("item.bid.denied", {
					price: formatPrice(bid.budget),
				});
			}

			if (!["DENIED", "ACCEPTED"].includes(bid?.status)) {
				excerpt = t("item.bid.open", {
					price: formatPrice(bid.budget),
					name: message?.creator?.first_name || "",
				});
			}
		}

		if (excerpt) {
			if (message.is_me) {
				content = `${t("item.i")}: ${excerpt}`;
			} else {
				if (message.creator) {
					content = `${message?.creator?.first_name}: ${excerpt}`;
				} else {
					content = excerpt;
				}
			}
		}
	}

	if (theme === "list-item") {
		return (
			<ListItem to={url} bordered align="start">
				<ListItem.Avatar size="small" src={user?.avatar} />
				<ListItem.Content className="gap-2 flex-1">
					<div className="flex flex-wrap gap-1 justify-between">
						<ListItem.Title className="relative flex items-center gap-2">
							{user?.name}
							{count > 0 && <Indicator>{count}</Indicator>}
						</ListItem.Title>
					</div>
					<ListItem.Text variant="small">{content}</ListItem.Text>
					{subject && (
						<ListItem.Text variant="small">
							<TitleCut
								color={
									auth.type === "student" ? "gray" : "white"
								}
								className="max-w-[180px]"
							>
								{subject}
							</TitleCut>
						</ListItem.Text>
					)}
				</ListItem.Content>
			</ListItem>
		);
	}

	return (
		<Card
			activeClassName="border-primary"
			className="border-2 border-white"
			to={url}
		>
			<ListItem align="start">
				<ListItem.Avatar size="small" src={user?.avatar} />
				<ListItem.Content className="gap-2">
					<div className="flex flex-wrap gap-1 justify-between">
						<div>
							<ListItem.Title>{user?.name}</ListItem.Title>
							{user?.company_name && (
								<small className="block opacity-40">
									{user.company_name}
								</small>
							)}
						</div>
						{count > 0 && <Indicator>{count}</Indicator>}
					</div>
					<ListItem.Text variant="small">{content}</ListItem.Text>
					<ListItem.Text variant="small">
						<div className="flex justify-between">
							<TitleCut className="w-40">
								<span>{subject}</span>
							</TitleCut>
							{message && (
								<span className="text-right opacity-20">
									{moment(message.created_at).fromNow()}
								</span>
							)}
						</div>
					</ListItem.Text>
				</ListItem.Content>
			</ListItem>
		</Card>
	);
};

InboxListItem.defaultProps = {
	theme: "card",
};

interface InboxListItemSkeletonProps {
	variant: "dark" | "light";
}

const InboxListItemSkeleton = ({ variant }: InboxListItemSkeletonProps) => {
	const bg = variant === "dark" ? "dark-accent" : "white";
	const skeletonBg = variant === "dark" ? "dark" : "gray-900";

	return (
		<div
			className={`flex flex-col gap-2 p-4 transition-all rounded-md bg-${bg}`}
		>
			<div className="flex gap-2">
				<div className="flex justify-center items-center">
					<Skeleton bg={skeletonBg} className="w-10 h-10" />
				</div>
				<div className="flex flex-1 gap-1 flex-col">
					<Skeleton bg={skeletonBg} className="w-2/4 h-5" />
					<Skeleton bg={skeletonBg} className="w-10 h-4" />
				</div>
			</div>
			<div className="flex flex-col">
				<Skeleton bg={skeletonBg} className="w-full h-4" />
				<Skeleton bg={skeletonBg} className="w-full h-4 mt-2" />
				<div className="flex justify-end mt-2">
					<Skeleton bg={skeletonBg} className="w-20 h-4" />
				</div>
			</div>
		</div>
	);
};

InboxListItem.Skeleton = InboxListItemSkeleton;

export default InboxListItem;
